// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — mineriumShop.js   ·   MYSTIC ALLY'S FURNISHINGS TILL
// ────────────────────────────────────────────────────────────────────
// The ONLY code path by which a furnishing changes hands. The UI never
// writes; it asks this file and renders the answer.
//
// Built to the same three storefront rules the Mark counter is built
// to, because they were learned the expensive way and they are not
// negotiable:
//
//   1 · READ THE PRICE FROM THE CATALOGUE, NEVER FROM THE CALLER.
//       A client that hands you a price is describing what it WANTS to
//       pay. That is the entire attack shape. Nothing in this file
//       takes a price parameter, and nothing ever may.
//   2 · REFUSING IS AN ANSWER, NOT AN ERROR. Every path resolves
//       { ok:false, reason, message } and never throws. A business rule
//       caught in a catch block reads as a fault in the logs forever.
//   3 · STAMP WHAT WAS PAID. `paidUD` plus `listPriceUD` on the record,
//       which is what later proves an acquisition settled honestly.
//
// ── ⚠ THE WRITE-TRUST GAP, AND WHY IT IS SMALL HERE ────────────────
// Beta rules validate a currency as "a non-negative number that did not
// jump too far" and nothing more, so ANY shop in this Empire reads a
// balance it cannot fully believe until server authority lands
// (`mass app/ie-functions/`). The Mark counter answered that by pricing
// everything at ◆1 during testing.
//
// This counter does NOT need that hedge, and it is worth saying why
// rather than copying the pattern out of habit: ✹ Dawgs are
// ACQUIRE-ONLY and buy nothing but furniture inside your own holding.
// A player who edits their own Dawgs balance has cheated themselves out
// of their own progression and has taken nothing from anybody — no
// finite good, no minted edition, nothing another player wanted. That
// is the one currency in the Empire it is safe to vend at full price
// before the ledger is sealed. ⚑ Note the shape of the argument: the
// risk is low because of what the money can REACH, not because we are
// feeling optimistic. If Dawgs ever buy something finite, this comment
// stops being true on that same day.
//
// ── ⚠ AMENDED 2026-09-13 · OW-21 · AND THE TILL IS AT ✹1 ANYWAY ──
// Everything above is still true and is why it is still written down.
// It is ALSO no longer the reason this shop prices the way it does.
// caP'n ruled a title-wide testing till (L52): **while a title is in
// testing, everything in it costs one unit of that title's lowest
// currency.** That is not a security hedge — it is so the whole
// acquisition path can be walked end to end without grinding for it.
//
// ⛑ SO THE PRICE LIVES IN `mineriumCatalogue.js`, NOT HERE. `TESTING`
// and `TESTING_PRICE_UD` sit beside `priceOf()` because `priceOf()` is
// what both the shelf and the till read. This file imports the answer;
// it does not decide it. When testing ends, ONE const flips and this
// counter needs no edit at all.
//
// ── FIREBASE (no new top-level node, no rules work) ────────────────
//   profiles/{uid}/minerium/stock/{itemId}  = n         placeables owned
//   profiles/{uid}/minerium/owned/{itemId}  = { … }     skies/grounds owned
//   profiles/{uid}/minerium/founded         = timestamp the free grant
//   profiles/{uid}/upDawgs                              the balance
//   homes/{uid}/minerium/sky | ground       = itemId    what is WORN
//
// ⚑ THE SPLIT, AND WHY IT IS NOT ARBITRARY. What a player OWNS lives on
// the PROFILE and travels with them. What a HOLDING LOOKS LIKE lives
// under `homes/{ownerUid}/`, because that is the node a VISITOR reads —
// the same instancing rule presence and the gallery frames already
// follow (PIERRE_PRIME L21, L22). A sky stored on the profile would be
// invisible to everyone who walked into your Minerium, which is the one
// thing a sky is for.
// ════════════════════════════════════════════════════════════════════

import { ref, onValue, get, update, increment } from "firebase/database";
import { db, auth } from "./firebase";
import {
  ITEMS, BUNDLES, SHELVES, isWorn, isWearable,
  priceOf, listPriceOf, bundlePriceOf, bundleListPriceOf,
  TESTING, TESTING_PRICE_UD,
  FOUNDING_GRANT, FOUNDING_SKY, FOUNDING_GROUND, FOUNDING_FLOOR, FOUNDING_WALL, FOUNDING_WORN,
  FOUNDING_PURSE_UD, FOUNDING_COMPANION, WORN_FIELD, FOUNDING_LIGHT,
} from "./mineriumCatalogue";
import { spendDawgs, grantDawgs } from "./upDawgs";

// ── THE TILL ────────────────────────────────────────────────────────
// One const per shelf. Do not scatter this decision across the UI — a
// shelf that is open in the shop and closed in the writer is how a
// player gets a refusal after a receipt.
export const VENDING = {
  fable:     "open",
  legendary: "open",
  mythical:  "open",
};

export const LOCK_REASON =
  "Bonnie will show you the piece, but she is not taking Dawgs for that shelf today.";

// ⛑ RE-EXPORTED so a screen asking "what does this cost" has exactly
// one import to reach for, and can never accidentally reach past the
// till to the raw catalogue field.
export { TESTING, TESTING_PRICE_UD, priceOf, listPriceOf, bundlePriceOf, bundleListPriceOf };

export const NO_FUNDS_REASON =
  "Not enough Dawgs. Your growers are working on it, and the Well up the boardwalk " +
  "will sell you some if you are impatient.";

// ── PATHS ───────────────────────────────────────────────────────────
const ownedPath = (uid, itemId) => `profiles/${uid}/minerium/owned/${itemId}`;
const MINERIUM  = (uid) => `profiles/${uid}/minerium`;        // what you OWN
const HOLDING   = (uid) => `homes/${uid}/minerium`;           // what it LOOKS LIKE

// ════════════════════════════════════════════════════════════════════
// READ
// ════════════════════════════════════════════════════════════════════

// { itemId: count } — placeables in the player's shed, not yet put down.
export function listenToStock(uid, cb) {
  if (!uid) { cb({}); return () => {}; }
  const off = onValue(ref(db, `${MINERIUM(uid)}/stock`), (snap) => {
    const raw = snap.val() || {};
    const out = {};
    // ⚠ FILTERED THROUGH THE CATALOGUE. A row for an item that has been
    // renamed or retired must not render as a mystery tile with no art
    // and no name — it simply stops appearing, and the row stays in
    // Firebase untouched so nothing is destroyed by a rename.
    for (const [id, n] of Object.entries(raw)) {
      if (ITEMS[id] && Number(n) > 0) out[id] = Math.floor(Number(n));
    }
    cb(out);
  });
  return () => off();
}

// { itemId: record } — skies and grounds, which are owned once and worn.
export function listenToOwned(uid, cb) {
  if (!uid) { cb({}); return () => {}; }
  const off = onValue(ref(db, `${MINERIUM(uid)}/owned`), (snap) => {
    const raw = snap.val() || {};
    const out = {};
    for (const [id, rec] of Object.entries(raw)) if (ITEMS[id]) out[id] = rec || true;
    cb(out);
  });
  return () => off();
}

export const ownsWorn = (owned, itemId) => !!(owned && owned[itemId]);
export const stockOf  = (stock, itemId) => Math.max(0, Math.floor(stock?.[itemId] || 0));

// ════════════════════════════════════════════════════════════════════
// ⭐ THE FOUNDING GRANT — free, once, and it never reads a balance
// ────────────────────────────────────────────────────────────────────
// A new Aenarchist has zero Dawgs, no door and nothing that pays. This
// is the bootstrap, and per THE FREE SHELF (L30) a free grant NEVER
// READS A BALANCE — it cannot fail on funds because it never asks about
// funds, which is exactly what makes it the safe way to exercise the
// whole acquisition path before the ledger is sealed.
//
// GRANT BEFORE FLAG: the goods land, then `founded` is stamped. A crash
// between them costs a duplicate founding, which is four crop beds and
// a shrug. The reverse costs a player their only way to start.
// ════════════════════════════════════════════════════════════════════
// ⚠ ONE FOUNDING IN FLIGHT PER PLAYER (2026-09-15). React StrictMode runs
// every mount effect TWICE in development, so `MineriumScreen` called this
// twice a few milliseconds apart — both reads saw no flag, both granted,
// and every dev holding opened with four beds and four doors. Read-then-
// grant is the law (GRANT BEFORE FLAG, above), so the dedupe is here: a
// second call while the first is still running gets the first's promise.
const _foundingInFlight = new Map();

export function foundHolding(uid) {
  if (_foundingInFlight.has(uid)) return _foundingInFlight.get(uid);
  const p = foundHoldingOnce(uid).finally(() => _foundingInFlight.delete(uid));
  _foundingInFlight.set(uid, p);
  return p;
}

// ⛑ THE FOUNDING REPAIR (2026-09-16). caP'n was founded on 09-14, when
// the rules had no write lane for homes/{uid}/minerium and before the
// welcome purse existed — so his holding had no sky, ground or companion
// and his wallet had never seen a Dawg. A founded flag must not freeze a
// half-founding forever. Both repairs are ONE-TIME:
//   · the holding defaults are written only where the holding has no sky
//   · the purse is paid only if `pursePaid` is absent (grant, then flag)
async function repairFounding(uid) {
  const out = { ok: false, reason: "already_founded", repaired: [] };
  try {
    const sky = await get(ref(db, `${HOLDING(uid)}/sky`));
    if (!sky.exists()) {
      await update(ref(db, HOLDING(uid)), {
        sky: FOUNDING_SKY, ground: FOUNDING_GROUND, hallFloor: FOUNDING_FLOOR, hallWall: FOUNDING_WALL, hallLight: FOUNDING_LIGHT,
        companion: { beingId: FOUNDING_COMPANION, tier: "beb" },
      });
      out.repaired.push("holding");
    }
  } catch { /* try again next visit */ }
  try {
    const paid = await get(ref(db, `${MINERIUM(uid)}/pursePaid`));
    if (!paid.exists()) {
      const got = await grantDawgs(uid, FOUNDING_PURSE_UD, "pierre:minerium:founding_repair");
      if (got > 0) {
        await update(ref(db, MINERIUM(uid)), { pursePaid: Date.now() });
        out.repaired.push("purse"); out.purseUD = got;
      }
    }
  } catch { /* try again next visit */ }
  return out;
}

async function foundHoldingOnce(uid) {
  if (!uid || auth.currentUser?.uid !== uid) return { ok: false, reason: "not_you" };

  try {
    const snap = await get(ref(db, `${MINERIUM(uid)}/founded`));
    if (snap.exists()) return repairFounding(uid);
  } catch {
    // A failed READ must never cause a double grant. If we cannot tell
    // whether they were founded, we do nothing and try again next visit.
    return { ok: false, reason: "unknown" };
  }

  const patch = {};
  for (const [itemId, n] of Object.entries(FOUNDING_GRANT)) {
    if (!ITEMS[itemId]) continue;
    patch[`stock/${itemId}`] = increment(n);
  }
  for (const itemId of FOUNDING_WORN) {
    if (!ITEMS[itemId]) continue;
    // ⚠ `listPriceUD` is the STICKER. It was being fed `priceOf()` — what
    // you PAY — which is right only while both founding items are free.
    // The day either gets a real price, every founding receipt would
    // have recorded the testing till's ✹1 as the thing's worth.
    patch[`owned/${itemId}`] = { source: "pierre:minerium:founding", paidUD: 0, listPriceUD: listPriceOf(itemId), at: Date.now() };
  }
  try { await update(ref(db, MINERIUM(uid)), patch); }
  catch { return { ok: false, reason: "grant_failed" }; }

  // The worn defaults go on the HOLDING, not the profile — a visitor
  // reads your sky, and a sky nobody else can see is not a sky. The
  // companion is the same kind of thing: at heel from the first minute,
  // visible to whoever walks in. (`setCompanion` is the same one write.)
  try {
    await update(ref(db, HOLDING(uid)), {
      sky: FOUNDING_SKY, ground: FOUNDING_GROUND, hallFloor: FOUNDING_FLOOR, hallWall: FOUNDING_WALL, hallLight: FOUNDING_LIGHT,
      companion: { beingId: FOUNDING_COMPANION, tier: "beb" },
    });
  } catch { /* the goods landed; the default sky re-applies on the next visit */ }

  // THE WELCOME PURSE — a gift, not a faucet: it rides the founded flag.
  let purse = 0;
  try { purse = await grantDawgs(uid, FOUNDING_PURSE_UD, "pierre:minerium:founding"); } catch { purse = 0; }

  // The flag, second and separately, exactly as the law says.
  try { await update(ref(db, MINERIUM(uid)), purse > 0 ? { founded: Date.now(), pursePaid: Date.now() } : { founded: Date.now() }); } catch { /* a re-grant is cheap */ }

  return { ok: true, granted: FOUNDING_GRANT, sky: FOUNDING_SKY, ground: FOUNDING_GROUND,
           companion: FOUNDING_COMPANION, purseUD: purse };
}

// ════════════════════════════════════════════════════════════════════
// BUY  —  one item
// ────────────────────────────────────────────────────────────────────
// Takes an ID and a quantity. Never a price. Never a shelf. Never a
// "free" flag.
// ════════════════════════════════════════════════════════════════════
export async function acquireItem(uid, itemId, qty = 1) {
  if (!uid || auth.currentUser?.uid !== uid) {
    return { ok: false, reason: "not_you", message: "You have to be signed in as yourself to buy anything." };
  }
  const it = ITEMS[itemId];
  if (!it) return { ok: false, reason: "no_such_item", message: "Bonnie has never stocked that." };

  const n = Math.floor(Number(qty) || 0);
  if (n < 1) return { ok: false, reason: "bad_qty", message: "Buy at least one." };
  // A worn thing is owned once. Buying a second Cracked Eclipse is not
  // a purchase, it is a mistake we should catch before taking money.
  if (isWorn(itemId) && n !== 1) {
    return { ok: false, reason: "bad_qty", message: "A sky is a sky. You only need the one." };
  }

  if ((VENDING[it.shelf] || "locked") !== "open") {
    return { ok: false, reason: "till_closed", message: LOCK_REASON };
  }

  if (isWorn(itemId)) {
    try {
      const owned = await get(ref(db, ownedPath(uid, itemId)));
      if (owned.exists()) return { ok: false, reason: "already_owned", message: isWearable(itemId) ? "That one is already yours. Wear it whenever you like." : "That layout is already yours. Apply it from FURNISH in your Hall." };
    } catch {
      // ⚠ WAS: fall through, "the write below is idempotent for worn
      // goods." It is not — it REPLACES the record, receipt and all. And
      // falling through reaches the charge, so a flaky read meant paying
      // again for a sky you already owned and losing the original
      // receipt in the same breath. Refusing costs one retry.
      return { ok: false, reason: "check_failed",
               message: "Bonnie could not check her book just now. Try again." };
    }
  }

  // ⭐ THE PRICE COMES FROM HERE, and there is no price parameter on
  // this function. There must never be one.
  // ⭐ THE PAIR. `priceOf` is what you PAY (✹1 while testing);
  // `listPriceOf` is what it is WORTH. Both are stamped on the receipt
  // below, which is what later proves what a purchase actually was.
  const unitPriceUD = priceOf(itemId) || 0;
  const listPriceUD = listPriceOf(itemId) || 0;
  const cost = unitPriceUD * n;

  // ── THE CHARGE. A delta on the leaf that ABORTS rather than clamping,
  // so "you cannot afford it" is decided by the database and not by a
  // number this client read a moment ago.
  let charged = 0;
  if (cost > 0) {
    charged = await spendDawgs(uid, cost);
    if (charged !== cost) {
      // spendDawgs aborts whole; a partial is impossible, but if the
      // shape ever changes this catches it instead of shipping goods.
      if (charged > 0) await grantDawgs(uid, charged, `refund:minerium:buy:${itemId}`);
      return { ok: false, reason: "insufficient", message: NO_FUNDS_REASON };
    }
  }

  // ── THE GRANT. ⚠ A PURCHASE INVERTS "grant before flag": the money is
  // already gone, so the failure to defend against is a paid-for chair
  // that never arrived. If the grant fails we REFUND, by the same delta
  // route, and we say so out loud.
  try {
    if (isWorn(itemId)) {
      await update(ref(db, `${MINERIUM(uid)}/owned`), {
        [itemId]: {
          source: "pierre:minerium", vendor: "mystic_ally", shelf: it.shelf,
          paidUD: charged, listPriceUD,
          testing: TESTING || null,       // …and that this was a test price
          at: Date.now(),
        },
      });
    } else {
      await update(ref(db, `${MINERIUM(uid)}/stock`), { [itemId]: increment(n) });
    }
  } catch {
    if (charged > 0) {
      const back = await grantDawgs(uid, charged, `refund:minerium:buy:${itemId}`);
      if (back !== charged) {
        return {
          ok: false, reason: "refund_failed",
          message: `The piece did not arrive and ✹${charged} could not be fully returned. Bonnie has written it down — nothing is lost, and the Pier will make it right.`,
        };
      }
    }
    return { ok: false, reason: "write_failed", message: "It slipped off the counter — your Dawgs are back. Try again." };
  }

  return { ok: true, itemId, qty: n, paidUD: charged, listPriceUD, testing: TESTING };
}

// ════════════════════════════════════════════════════════════════════
// BUY  —  a bundle
// ────────────────────────────────────────────────────────────────────
// ⚑ THE WHOLE BUNDLE LANDS IN ONE WRITE. Every line is an `increment()`
// inside a single multi-path `update()`, which Firebase applies
// atomically — so a bundle can never half-arrive, and two bundles
// bought at the same instant cannot clobber each other's counts the way
// a read-modify-write would. This is the same reason the rest of the
// Empire moves money with deltas.
// ════════════════════════════════════════════════════════════════════
export async function acquireBundle(uid, bundleId) {
  if (!uid || auth.currentUser?.uid !== uid) {
    return { ok: false, reason: "not_you", message: "You have to be signed in as yourself to buy anything." };
  }
  const b = BUNDLES[bundleId];
  if (!b) return { ok: false, reason: "no_such_item", message: "Bonnie has never packed that one." };

  if ((VENDING[b.shelf] || "locked") !== "open") {
    return { ok: false, reason: "till_closed", message: LOCK_REASON };
  }

  // ⚠ NOT `b.priceUD`. Reading the raw catalogue field here is exactly
  // how a crate stays at full price while every single on the same shelf
  // drops to ✹1 — the tag and the till disagreeing, in one line.
  const chargePriceUD = bundlePriceOf(bundleId) || 0;
  const listPriceUD   = bundleListPriceOf(bundleId) || 0;

  // Build the grant BEFORE taking any money, so a malformed bundle is
  // caught while the player still has their Dawgs.
  const stockPatch = {};
  const wornPatch  = {};
  for (const [itemId, count] of Object.entries(b.contents || {})) {
    const it = ITEMS[itemId];
    if (!it) return { ok: false, reason: "bad_bundle", message: "That bundle is missing a piece. Bonnie has not taken your Dawgs for it." };
    if (isWorn(itemId)) {
      // ⚠ A RECEIPT IS EVIDENCE, AND THIS USED TO OVERWRITE ONE.
      // `bundle_ascendant` contains `sky_the_rise`. Buy THE RISE on its
      // own (record: paidUD 90000), then buy the crate, and the record
      // was replaced with `paidUD: 0, source: bundle` — the proof of the
      // 90,000 purchase simply gone. Player-earned records are
      // append-only; a second acquisition of a thing you own once adds
      // nothing and must destroy nothing. The duplicate is dropped
      // below, after the ownership read. Found by adversarial review
      // 2026-09-13.
      wornPatch[itemId] = {
        source: `pierre:minerium:bundle:${bundleId}`, vendor: "mystic_ally",
        shelf: it.shelf, paidUD: 0, listPriceUD: listPriceOf(itemId),
        testing: TESTING || null, at: Date.now(),
      };
    } else {
      stockPatch[itemId] = increment(Math.floor(Number(count) || 0));
    }
  }
  if (!Object.keys(stockPatch).length && !Object.keys(wornPatch).length) {
    return { ok: false, reason: "bad_bundle", message: "That bundle is empty. Bonnie has not taken your Dawgs for it." };
  }

  const charged = chargePriceUD > 0 ? await spendDawgs(uid, chargePriceUD) : 0;
  if (chargePriceUD > 0 && charged !== chargePriceUD) {
    if (charged > 0) await grantDawgs(uid, charged, `refund:minerium:bundle:${bundleId}`);
    return { ok: false, reason: "insufficient", message: NO_FUNDS_REASON };
  }

  // ⭐ Drop any worn line the player ALREADY owns, so an existing receipt
  // is never written over. Read once, immediately before the write.
  // ⚠ A FAILED READ DROPS THE LINE rather than overwriting blind — the
  // player keeps the record they have, and keeps the sky they already
  // own. Losing a duplicate grant of a thing you own is free; losing the
  // receipt is not.
  if (Object.keys(wornPatch).length) {
    for (const itemId of Object.keys(wornPatch)) {
      try {
        const owned = await get(ref(db, ownedPath(uid, itemId)));
        if (owned.exists()) delete wornPatch[itemId];
      } catch { delete wornPatch[itemId]; }
    }
  }

  const patch = {};
  for (const [k, v] of Object.entries(stockPatch)) patch[`stock/${k}`] = v;
  for (const [k, v] of Object.entries(wornPatch))  patch[`owned/${k}`] = v;

  // ⚠ THE EMPTINESS CHECK HAS TO RUN **AGAIN**, HERE. The one above runs
  // before the charge; the dedup loop that drops already-owned worn
  // lines runs after it. A bundle of nothing but skies you already own
  // therefore reached this point as `update(ref, {})` — which resolves
  // perfectly happily — with the money already taken.
  // Found by adversarial review 2026-09-14.
  if (!Object.keys(patch).length) {
    if (charged > 0) await grantDawgs(uid, charged, `refund:minerium:bundle:${bundleId}`);
    return { ok: false, reason: "already_owned",
             message: "You already own everything in that crate — nothing was charged." };
  }

  try {
    await update(ref(db, MINERIUM(uid)), patch);
  } catch {
    if (charged > 0) {
      const back = await grantDawgs(uid, charged, `refund:minerium:bundle:${bundleId}`);
      if (back !== charged) {
        return {
          ok: false, reason: "refund_failed",
          message: `The bundle did not arrive and ✹${charged} could not be fully returned. Bonnie has written it down — nothing is lost, and the Pier will make it right.`,
        };
      }
    }
    return { ok: false, reason: "write_failed", message: "The crate did not open — your Dawgs are back. Try again." };
  }

  return { ok: true, bundleId, paidUD: charged, listPriceUD, testing: TESTING, contents: b.contents };
}

// ════════════════════════════════════════════════════════════════════
// WEAR  —  a sky or a ground
// ────────────────────────────────────────────────────────────────────
// No money moves in this function, and there is no currency write path
// in it. It is the OWNERSHIP CHECK in front of one field.
// ════════════════════════════════════════════════════════════════════
export async function wearItem(uid, itemId) {
  if (!uid || auth.currentUser?.uid !== uid) {
    return { ok: false, reason: "not_you", message: "You can only redress your own holding." };
  }
  const it = ITEMS[itemId];
  if (!it || !isWorn(itemId)) return { ok: false, reason: "no_such_item", message: "That is not something you wear over a holding." };
  // ⚠ OW-42: a layout is owned like a finish but has no field to wear it
  // in — WORN_FIELD's fallback would have written it over the SKY.
  if (!isWearable(itemId)) return { ok: false, reason: "not_wearable", message: "A layout is built, not worn — apply it from FURNISH in your Hall." };

  // ⚠⚠ A FREE DEFAULT NEEDS NO RECEIPT (OW-34 review, 2026-09-16). The
  // FURNISH panel has always shown a ✹0 finish as yours ("have" is
  // `owned || priceUD === 0`), but this check wanted the receipt — and
  // every holding founded before the finishes existed (caP'n's, 09-14)
  // has none. Wear Void Slate, then try to go back to White Marble:
  // "You do not own that one yet." A free thing is owned by everybody.
  if (listPriceOf(itemId) !== 0) {
    try {
      const owned = await get(ref(db, ownedPath(uid, itemId)));
      if (!owned.exists()) return { ok: false, reason: "not_owned", message: "You do not own that one yet." };
    } catch {
      return { ok: false, reason: "unknown", message: "Could not check that one. Try again." };
    }
  }

  const field = WORN_FIELD[it.kind];
  if (!field) return { ok: false, reason: "not_wearable", message: "That is not something you wear over a holding." };
  try { await update(ref(db, HOLDING(uid)), { [field]: itemId }); }
  catch { return { ok: false, reason: "write_failed", message: "It would not settle. Try again." }; }
  return { ok: true, itemId, field };
}

// ════════════════════════════════════════════════════════════════════
// THE STOCK LIST — what Ally has on a shelf, with the till's state
// attached so the UI never decides policy for itself.
// ════════════════════════════════════════════════════════════════════
export function shelfStock(shelf) {
  return {
    shelf,
    state: VENDING[shelf] || "locked",
    bundles: Object.values(BUNDLES).filter((b) => b.shelf === shelf),
    items:   Object.values(ITEMS).filter((i) => i.shelf === shelf),
  };
}

export const allShelves = () => SHELVES.map(shelfStock);

const mineriumShopApi = {
  VENDING, listenToStock, listenToOwned, ownsWorn, stockOf,
  foundHolding, acquireItem, acquireBundle, wearItem,
  shelfStock, allShelves,
};

export default mineriumShopApi;
